import { RiComputerLine } from "@remixicon/react";
import { useEffect, useRef, useState } from "react";
import type { SettingsReason } from "@/hooks/use-screen";
import { metascalpLink } from "@/terminal/metascalp-link";
import { terminalName } from "@/terminal/terminal-action";
import {
  type TerminalSettings,
  terminalSettingsStore,
  useTerminalSettings,
} from "@/terminal/terminal-settings";
import { TerminalSettingsContent } from "@/terminal/terminal-settings-content";
import {
  draftForOpening,
  draftsEqual,
  type LiveMarket,
  saveTerminalDraft,
  type TerminalSettingsDraft,
  validateDraft,
} from "@/terminal/terminal-settings-draft";
import { SettingsSection } from "./settings-section";

export const TERMINAL_SECTION_LABEL = "Терминал";
const TERMINAL_SECTION_DESCRIPTION =
  "Открытие инструмента в торговом терминале. Настройки хранятся на этом устройстве.";
const SAVE_REJECTED_TEXT = "Настройки не сохранены: проверьте поля раздела.";

/** Сохранённое состояние для подвала: что выбрано и куда стучится. */
function savedSummary(settings: TerminalSettings): string {
  const name = terminalName(settings.terminal);
  if (!settings.enabled) {
    return `${name} выключен`;
  }
  if (settings.terminal === "metascalp") {
    const { port } = settings.metascalp;
    return `${name} · ${port === null ? "порт ищется сам" : `порт ${port}`}`;
  }
  return `${name} · ${settings.host}:${settings.port}`;
}

export interface TerminalSettingsSectionProps {
  reason: SettingsReason | null;
  /** Рынки живых плотностей: строки таблицы соответствия. Спрашиваются один раз, при открытии страницы. */
  getLiveMarkets: () => readonly LiveMarket[];
  /**
   * Блок Telegram выше раздела вышел из загрузки (в любое состояние). Пока он
   * не достроен, он растёт и сталкивает раздел из поля зрения, поэтому
   * прокрутка и фокус по поводу ждут его.
   */
  telegramSettled?: boolean;
}

/**
 * Раздел «Терминал» (§127 п. 7, §49, §89): тот же блок, что был в листе
 * экрана плотностей. Черновик строится эффектом после монтирования — к этому
 * моменту экран плотностей уже зарегистрировал источник рынков в своём
 * layout-эффекте; поток поля под руками не перестраивает. «Сохранить»
 * остаётся на странице и отвечает надписью «сохранено».
 */
export function TerminalSettingsSection({
  reason,
  getLiveMarkets,
  telegramSettled = true,
}: TerminalSettingsSectionProps) {
  const [settings] = useTerminalSettings();
  const [draft, setDraft] = useState<TerminalSettingsDraft | null>(null);
  // Черновик на момент открытия или последнего сохранения: «Отменить» возвращает к нему,
  // «Сохранить» активна только при отличии от него.
  const [baseline, setBaseline] = useState<TerminalSettingsDraft | null>(null);
  const [savedNotice, setSavedNotice] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [findStatus, setFindStatus] = useState("");
  const [finding, setFinding] = useState(false);
  const section = useRef<HTMLElement | null>(null);
  const groupsInput = useRef<HTMLInputElement>(null);
  // Поколение страницы: поиск, начатый до ухода, итог не пишет.
  const generation = useRef(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: черновик строится один раз при открытии страницы, а не на каждое обновление настроек.
  useEffect(() => {
    const opened = draftForOpening(settings, getLiveMarkets);
    setDraft(opened);
    setBaseline(opened);
  }, []);

  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );

  // Повод из адреса: раздел прокручивается в поле зрения, а по поводу
  // «группы» затем фокус уходит в поле набора (поле есть только у TigerTrade).
  const ready = draft !== null && telegramSettled;
  useEffect(() => {
    if (reason === null || !ready) {
      return;
    }
    section.current?.scrollIntoView({ block: "start" });
    if (reason === "groups") {
      groupsInput.current?.focus({ preventScroll: true });
    }
  }, [reason, ready]);

  /** Правка черновика гасит надпись «сохранено» и прошлую ошибку. */
  const edit = (change: (previous: TerminalSettingsDraft) => TerminalSettingsDraft) => {
    setSavedNotice(false);
    setSaveError(undefined);
    setDraft((previous) => (previous === null ? previous : change(previous)));
  };

  const handleFindPort = () => {
    if (finding) {
      return;
    }
    const started = generation.current;
    setFinding(true);
    setFindStatus("Ищем MetaScalp…");
    void metascalpLink.find().then((port) => {
      if (started !== generation.current) {
        return;
      }
      setFinding(false);
      setFindStatus(port === null ? "MetaScalp не найден" : `Найден порт ${port}`);
    });
  };

  const handleSave = () => {
    if (draft === null) {
      return;
    }
    const saved = saveTerminalDraft(draft, terminalSettingsStore);
    setSavedNotice(saved);
    setSaveError(saved ? undefined : SAVE_REJECTED_TEXT);
    if (saved) {
      setBaseline(draft);
    }
  };

  const handleCancel = () => {
    setSavedNotice(false);
    setSaveError(undefined);
    setDraft(baseline);
  };

  return (
    <SettingsSection
      sectionId="terminal"
      icon={<RiComputerLine className="size-4" />}
      label={TERMINAL_SECTION_LABEL}
      description={TERMINAL_SECTION_DESCRIPTION}
      cardRef={(element) => {
        section.current = element;
      }}
    >
      {draft === null ? null : (
        <TerminalSettingsContent
          draft={draft}
          errors={validateDraft(draft)}
          onChange={(patch) => edit((previous) => ({ ...previous, ...patch }))}
          onRowChange={(key, patch) =>
            edit((previous) => ({
              ...previous,
              rows: previous.rows.map((row) => (row.key === key ? { ...row, ...patch } : row)),
            }))
          }
          onMetascalpRowChange={(key, patch) =>
            edit((previous) => ({
              ...previous,
              metascalpRows: previous.metascalpRows.map((row) =>
                row.key === key ? { ...row, ...patch } : row,
              ),
            }))
          }
          onFindPort={handleFindPort}
          findStatus={findStatus}
          finding={finding}
          onSave={handleSave}
          unchanged={baseline !== null && draftsEqual(draft, baseline)}
          onCancel={handleCancel}
          statusText={savedSummary(settings)}
          savedNotice={savedNotice}
          saveError={saveError}
          hint={reason === "groups" ? "groups" : undefined}
          groupsInputRef={groupsInput}
        />
      )}
    </SettingsSection>
  );
}
